import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useRiqueChatStore } from '@/stores/riqueChatStore';
import { useRiqueChat } from '@/hooks/useRiqueChat';
import { fetchRiqueAlerts, fetchRiqueBriefing } from '@/lib/riqueApi';
import { resolveRiqueRouteContext } from '@/lib/riqueContext';
import { RiqueModuleLayout } from '@/components/Rique/RiqueModuleLayout';
import { RiqueEmailDialog } from '@/components/Rique/RiqueEmailDialog';
import { UserRole } from '@/types';

/** Módulo Rique.Ia — página cheia estilo ChatGPT. */
export const RiquePage: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const isGestorOrAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.GESTOR;

  const companyId = useRiqueChatStore((s) => s.companyId);
  const setCompanyId = useRiqueChatStore((s) => s.setCompanyId);
  const setPendingPrompt = useRiqueChatStore((s) => s.setPendingPrompt);
  const setRoutePath = useRiqueChatStore((s) => s.setRoutePath);
  const setPageContext = useRiqueChatStore((s) => s.setPageContext);

  const [emailOpen, setEmailOpen] = React.useState(false);

  const {
    companies,
    conversations,
    activeId,
    setActiveId,
    activeConversation,
    messages,
    streaming,
    loadingReply,
    progressLabel,
    lastAssistant,
    unavailable,
    llmUnavailable,
    statusLoading,
    statusUnknown,
    refetchStatus,
    handleSend,
    stopReply,
    handleSendImage,
    handleNewConversation,
    handleDeleteConversation,
    handleDeleteAllConversations,
  } = useRiqueChat(isGestorOrAdmin);

  React.useEffect(() => {
    const ctx = resolveRiqueRouteContext('/rique', '');
    setRoutePath('/rique');
    setPageContext(ctx.pageContext);
  }, [setRoutePath, setPageContext]);

  // ?prompt= da URL → pendingPrompt (consumido por useRiqueChat)
  React.useEffect(() => {
    const prompt = searchParams.get('prompt')?.trim();
    if (!prompt) return;
    setPendingPrompt(prompt);
    const next = new URLSearchParams(searchParams);
    next.delete('prompt');
    next.delete('openRique');
    setSearchParams(next, { replace: true });
  }, [searchParams, setPendingPrompt, setSearchParams]);

  const { data: alertsData } = useQuery({
    queryKey: ['rique-alerts', companyId],
    queryFn: () => fetchRiqueAlerts(companyId || undefined),
    enabled: isGestorOrAdmin,
    staleTime: 120_000,
  });

  const { data: briefingData } = useQuery({
    queryKey: ['rique-briefing', companyId],
    queryFn: () => fetchRiqueBriefing(companyId || undefined),
    enabled: isGestorOrAdmin,
    staleTime: 300_000,
  });

  const briefing = briefingData?.live ?? briefingData?.cached;
  const selectedCompanyName = companies?.find((c) => c.id === companyId)?.name;
  const routeCtx = resolveRiqueRouteContext('/rique', '');

  if (!isGestorOrAdmin) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <>
      <RiqueModuleLayout
        companies={companies}
        companyId={companyId}
        setCompanyId={setCompanyId}
        conversations={conversations}
        activeId={activeId}
        setActiveId={setActiveId}
        activeConversation={activeConversation}
        messages={messages}
        streaming={streaming}
        loadingReply={loadingReply}
        progressLabel={progressLabel}
        unavailable={unavailable}
        llmUnavailable={llmUnavailable}
        statusLoading={statusLoading}
        statusUnknown={statusUnknown}
        onRetryStatus={() => {
          void refetchStatus();
        }}
        alerts={alertsData?.live}
        briefing={briefing}
        routeSuggestions={routeCtx.suggestions}
        lastAssistantText={lastAssistant}
        onSend={handleSend}
        onStop={stopReply}
        onSendImage={handleSendImage}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
        onDeleteAllConversations={handleDeleteAllConversations}
        onEmailClick={() => setEmailOpen(true)}
        userName={user?.name ?? user?.email}
        companyName={selectedCompanyName}
      />
      <RiqueEmailDialog
        isOpen={emailOpen}
        onClose={() => setEmailOpen(false)}
        conversationId={activeId}
        companyId={companyId || undefined}
        defaultEmail={user?.email ?? ''}
      />
    </>
  );
};
